import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { format, subDays } from "date-fns";
import DateModal from "@/pages/dashboard/components/date-modal";
import { CustomersSearch } from "@/sections/customer/customers-search";
import states from "@/constants/states";
import {
  useGetAiSearchLogsQuery,
  useGetAiSearchOverviewQuery,
} from "@/app/features/aiSearchAnalytics/aiSearchAnalyticsApiSlice";
import PopularItemsCard from "./components/PopularItemsCard";
import AiSearchLogListing from "./components/AiSearchLogListing";

function SelectStateDropdown({ selectedState, handleSelectedStateChange }) {
  return (
    <FormControl fullWidth>
      <InputLabel id="ai-search-state-label">State</InputLabel>
      <Select
        labelId="ai-search-state-label"
        id="ai-search-state-select"
        value={selectedState}
        label="State"
        onChange={handleSelectedStateChange}
        variant="outlined"
      >
        <MenuItem value="All">All</MenuItem>
        {states.map((item) => (
          <MenuItem value={item.code} key={item.code}>
            {item.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default function AiSearchAnalyticsPage() {
  const [search, setSearch] = useState("");
  const [isDateModalVisible, setIsDateModalVisible] = useState(false);
  const [selectedState, setSelectedState] = useState("All");
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 20,
    page: 0,
  });
  const [dateState, setDateState] = useState([
    {
      startDate: subDays(new Date(), 30),
      endDate: new Date(),
      key: "selection",
    },
  ]);
  const [rowCountState, setRowCountState] = useState(0);

  const startDate = dateState[0].startDate.toISOString();
  const endDate = dateState[0].endDate.toISOString();
  const state = selectedState === "All" ? "" : selectedState.toLowerCase();

  const {
    data: overviewResponse,
    isLoading: isOverviewLoading,
    error: overviewError,
  } = useGetAiSearchOverviewQuery({
    startDate,
    endDate,
    state,
  });

  const { data, isLoading, isFetching, error } = useGetAiSearchLogsQuery({
    page: paginationModel.page + 1,
    limit: paginationModel.pageSize,
    search,
    startDate,
    endDate,
    state,
  });

  useEffect(() => {
    setRowCountState((prev) =>
      data?.data?.total !== undefined ? data.data.total : prev
    );
  }, [data]);

  useEffect(() => {
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [search]);

  const overview = overviewResponse?.data;
  const summary = overview?.summary || { totalSearches: 0, uniqueQueries: 0 };

  return (
    <Box component="main" sx={{ flexGrow: 1, py: 8 }}>
      <DateModal
        open={isDateModalVisible}
        handleClose={() => setIsDateModalVisible(false)}
        handleDateChange={(item) => {
          setDateState([item.selection]);
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }}
        dateState={dateState}
      />

      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h4">AI Search Analytics</Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                See what people type into Find My Car and which filters those
                searches apply.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => setIsDateModalVisible(true)}
              >
                {`${format(dateState[0].startDate, "dd MMM yyyy")} - ${format(
                  dateState[0].endDate,
                  "dd MMM yyyy"
                )}`}
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SelectStateDropdown
                selectedState={selectedState}
                handleSelectedStateChange={(event) => {
                  setSelectedState(event.target.value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
              />
            </Grid>
          </Grid>

          {overviewError ? (
            <Typography>Something went wrong loading overview stats.</Typography>
          ) : isOverviewLoading ? (
            <Skeleton height={110} sx={{ transform: "none" }} />
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Card sx={{ p: 3 }}>
                  <Typography color="text.secondary" variant="overline">
                    Total searches
                  </Typography>
                  <Typography variant="h4">{summary.totalSearches}</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card sx={{ p: 3 }}>
                  <Typography color="text.secondary" variant="overline">
                    Unique queries
                  </Typography>
                  <Typography variant="h4">{summary.uniqueQueries}</Typography>
                </Card>
              </Grid>
            </Grid>
          )}

          <Typography variant="h5">Popular searches and filters</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={4}>
              <PopularItemsCard
                title="What people typed"
                emptyLabel="No typed searches in this date range."
                items={overview?.popularQueries || []}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <PopularItemsCard
                title="Popular brands"
                emptyLabel="No brand filters in this date range."
                items={overview?.popularMakes || []}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <PopularItemsCard
                title="Popular body types"
                emptyLabel="No body filters in this date range."
                items={overview?.popularBodies || []}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <PopularItemsCard
                title="Popular fuel types"
                emptyLabel="No fuel filters in this date range."
                items={overview?.popularFuels || []}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <PopularItemsCard
                title="Popular seat counts"
                emptyLabel="No seat filters in this date range."
                items={overview?.popularSeats || []}
              />
            </Grid>
          </Grid>

          <CustomersSearch
            search={search}
            setSearch={setSearch}
            placeholder="Search what people typed"
          />

          <AiSearchLogListing
            isLoading={isLoading || isFetching}
            error={error}
            data={data?.data}
            rowCountState={rowCountState}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
          />
        </Stack>
      </Container>
    </Box>
  );
}
